/**
 * @file       Setting service for entire system
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */

import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { MULTIFACTOR_VALUE, SETTING_CODE_REF,
    DOCUMENT_VIEW_VALUE } 
    from '@app/constants/Setting.constant';
import { SETTING_API } from '@app/constants/Api.constant';
import { ERROR } from '@app/constants/Error.constant';
import { SettingInf } from '@app/secure/interface/Setting.interface';
import { Setting } from '@app/model/setting';
import { ApiService } from '@app/secure/service/Api.service';
import { AuthService } from '@app/service/auth.service';

@Injectable
({
    providedIn: 'root'
})
class SettingService 
{
    public settings$: BehaviorSubject<SettingInf[]> = 
        new BehaviorSubject<SettingInf[]>([]);
        
    /**
     * @desc - service constructor
     * @access - public
     * @param - apiService : ApiService
     * @return
     */
    constructor
    (
        private apiService: ApiService,
        private injector: Injector
    )
    {
        let storedSettings: any;
        let parsedSettings: any;

        try
        {
            storedSettings = localStorage.getItem('settings');
            if (storedSettings) 
            {
                parsedSettings = JSON.parse(storedSettings);
   
                this.settings$.next(Array.isArray(parsedSettings) ? 
                parsedSettings : parsedSettings?.settings ||
                    parsedSettings?.cyData?.settings || []);
            }
        }
        catch (error)
        {
            console.error('SettingService init localStorage:', error);
        }
    }

    /**
     * @desc   - settings array: in-memory first (localStorage may be empty
     *           or blocked), then persisted storage
     * @access - private
     * @param  -
     * @return - SettingInf[]
     */
    private getSettingsArray(): SettingInf[]
    {
        let fromMemory: SettingInf[];
        let stored: string | null;
        let parsed: any;
        let interim: any;
        let result: SettingInf[];

        fromMemory = this.settings$.value;
        stored = null;
        parsed = null;
        interim = null;
        result = [];

        if (fromMemory && fromMemory.length > 0)
        {
            result = fromMemory;
        }
        else
        {
            try
            {
                stored = localStorage.getItem('settings');
                if (stored)
                {
                    parsed = JSON.parse(stored);
                    interim = Array.isArray(parsed) ?
                        parsed :
                        (parsed?.settings ||
                            parsed?.cyData?.settings ||
                            []);
                    if (Array.isArray(interim))
                    {
                        result = interim;
                    }
                }
            }
            catch (error)
            {
                console.error('getSettingsArray read error:', error);
                result = [];
            }
        }

        return result;
    }

    /**
     * @desc - set the settings
     * @access - public
     * @param - settings : Setting
     * @return -
     */
    setSettings
    (
        settings: SettingInf[]
    )
    {
        this.settings$.next(settings);
        try
        {
            if (!settings || settings.length === 0)
            {
                localStorage.removeItem('settings');
            }
            else
            {
                localStorage.setItem(
                    'settings',
                    JSON.stringify(settings)
                );
            }
        }
        catch (error)
        {
            console.error('setSettings localStorage:', error);
        }
    }

    /**
     * @desc   - clear cached settings after logout
     * @access - public
     * @param  -
     * @return -
     */
    clearPersistedStateOnLogout
    (
    )
    {
        this.settings$.next([]);
        try
        {
            localStorage.removeItem('settings');
            localStorage.removeItem('taskNumberGen');
        }
        catch (error)
        {
            console.error('clearPersistedStateOnLogout:', error);
        }
    }

    private isAuthSessionActive
    (
    ): boolean
    {
        let auth: AuthService | null;
        let result: boolean;

        auth = null;
        result = false;
        try
        {
            auth = this.injector.get(AuthService);
        }
        catch (e)
        {
            auth = null;
        }

        if (auth && auth.userValue)
        {
            result = true;
        }

        return result;
    }

    /**
     * @desc - gets the settings from the server
     * @access - public
     * @param -
     * @return - Observable<>
     */
    getSettings() 
    {
        let settings: any;

        this.apiService.post<any>(SETTING_API.GET_SETTINGS, {})
        .subscribe(
        {
            next: (response) => 
            {
                if (!this.isAuthSessionActive())
                {
                    return;
                }

                settings =
                    response?.cyData?.settings ||
                    response?.settings ||
                    [];
                this.setSettings(settings);
            },
            error: (err) => 
            {
                console.error('getSettings failed:', err);
            }
        });
    }

    /**
     * @desc - update system settings
     * @access - public
     * @param - postData - post data contains settings array
     * @return - Observable<>
     */
    public updateSettings
    (
        postData: any
    ) : Observable<any>
    {
        let serviceObj: any;
        let cyResult: any;

        try
        {
            serviceObj =
                    this.apiService
                    .post<any>(SETTING_API.UPDATE_SETTINGS, postData)
            .pipe(map((data: any) =>
            {
                cyResult =
                {
                    cyData: {},
                    errorCode: ERROR.NO_ERROR,
                    errorMessage: null
                };
                cyResult.cyData = data;

                return cyResult;
            }),
            catchError((err) =>
            {
                let errorParts: any;

                errorParts = err.message.split(',');
                cyResult = 
                { 
                    updated: false,
                    errorCode: null,
                    errorMessage: null 
                };

                if (errorParts && errorParts.length > 1 && errorParts[0])
                {
                    switch (errorParts[1])
                    {
                        case ERROR.GENERAL_DB:
                            cyResult.errorCode = errorParts[1];
                            cyResult.errorMessage = 'General DB Error';
                            break;
                        default:
                            cyResult.errorCode = errorParts[1];
                            cyResult.errorMessage = 'General Error';
                            break;
                    }
                }

                return of(cyResult);
            }));
        }
        catch (e)
        {
            console.log(e);
            cyResult =
            {
                errorCode: ERROR.GENERAL,
                errorMessage: 'General Error'
            };

            return of(cyResult);
        }

        return serviceObj;
    }

    public getPublicSsoEnabled(): Observable<boolean>
    {
        let serviceObj: Observable<boolean>;

        serviceObj = this.apiService
            .get<any>(SETTING_API.GET_PUBLIC_SSO_ENABLED)
        .pipe(
            map((response: any) =>
            {
                let enabled: boolean;

                enabled = false;
                enabled = response?.ssoEnabled === true;

                return enabled;
            }),
            catchError(() =>
            {
                return of(false);
            })
        );

        return serviceObj;
    }
    
    /**
     * @desc - sets the task number generation
     * @access - public
     * @param - value : string
     * @return
     */
    public setTaskNumberGen
    (
        value
    )
    {
        localStorage.setItem('taskNumberGen', value);
    }
    
    /**
     * @desc - gets the task number generation
     * @access - public
     * @param -
     * @return - object : taskNumberGen
     */
    public getTaskNumberGen()
    {
        let taskNumberGen;
        
        taskNumberGen = localStorage.getItem('taskNumberGen') ? 
                        localStorage.getItem('taskNumberGen') : null;
        
//        if (taskNumberGen)
//        {
//            this.taskNumberGen$.next(taskNumberGen)
//        }
        
        return taskNumberGen;
    }
    
    /**
     * @desc - checks if the task number generation is auto
     * @access - public
     * @param -
     * @return - boolean : taskNumberAutoGen
     */
    public isTaskNumberAutoGen()
    {
        let taskNumberAutoGen : boolean;
        let taskNumberGen : any;
    
        // initialize variables
        taskNumberAutoGen = false;        
        taskNumberGen = this.getTaskNumberGen();
        
        if (taskNumberGen)
        {
            taskNumberAutoGen = Setting.isTaskNumberAutoGen(taskNumberGen)    
        }
        
        return taskNumberAutoGen;
    }

    /**
     * @desc   - load system settings from the server
     * @access - public
     * @param  - boolean forceReload : reload even when cached
     * @return - Promise<void>
     */
    public async loadSettings
    (
        forceReload: boolean = false
    )
    {
        let data: any;
        let response: any;
        let shouldReload: boolean;
        let settings: SettingInf[];
        let taskNumberSetting: any;

        try
        {
            shouldReload = false;
            settings = null;

            shouldReload =
                forceReload ||
                !this.settings$.value ||
                this.settings$.value.length === 0;

            if (shouldReload)
            {
                data = {};
                response = await this.apiService.post<any>(
                    SETTING_API.GET_SETTINGS,
                    data
                ).toPromise();

                settings =
                    response?.cyData?.settings ||
                    response?.settings ||
                    null;

                if (Array.isArray(settings))
                {
                    this.setSettings(settings);
                    taskNumberSetting =
                        Setting.getTaskNumberCode(settings);

                    if (taskNumberSetting)
                    {
                        this.setTaskNumberGen(
                            taskNumberSetting.value
                        );
                    }
                }
            }
        }
        catch (error)
        {
            console.error('loadSettings failed:', error);
        }
    }

    /**
     * @desc   - true when global MFA is email, SMS, or user choice
     * @access - public
     * @param  -
     * @return - boolean : multiFactorEnabled
     */
    public isMultiFactorEnabled(): boolean 
    {
        let multiFactorEnabled: boolean;
        let settings: SettingInf[];
        let multiFactorSetting: SettingInf | undefined;
        let settingValue: string;

        multiFactorEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            multiFactorSetting = settings.find((item: SettingInf) =>
                Number(item.code) ===
                    SETTING_CODE_REF.MULTIFACTOR_VERIFICATION_CODE
            );

            if (multiFactorSetting)
            {
                settingValue = String(multiFactorSetting.value);
                multiFactorEnabled =
                    settingValue ===
                        MULTIFACTOR_VALUE.EMAIL_VERIFICATION ||
                    settingValue ===
                        MULTIFACTOR_VALUE.SMS_VERIFICATION ||
                    settingValue ===
                        MULTIFACTOR_VALUE.EMAIL_AND_SMS_VERIFICATION;
            }
        }

        return multiFactorEnabled;
    }

    /**
     * @desc - checks if the Jury System is enabled
     * @access - public
     * @param -
     * @return - boolean : jurySystemEnabled
     */
    public isJurySystemEnabled(): boolean 
    {
        let jurySystemEnabled: boolean;
        let settings: any;
        let jurySetting: any;

        jurySystemEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            jurySetting = settings.find((item: any) =>
                Number(item.code) ===
                    SETTING_CODE_REF.JURY_SYSTEM_CODE &&
                item.value === "1"
            );
            jurySystemEnabled = !!jurySetting;
        }

        return jurySystemEnabled;
    }

    /**
     * @desc   - checks if the Court Management System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : courtSystemEnabled
     */
    public isCourtSystemEnabled(): boolean
    {
        let courtSystemEnabled: boolean;
        let settings: any;
        let courtSetting: any;

        courtSystemEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            courtSetting =
                settings.find(
                    (item: any) =>
                        Number(item.code) ===
                        SETTING_CODE_REF.COURT_SYSTEM_CODE
                        && item.value === "1"
                );
            courtSystemEnabled = !!courtSetting;
        }

        return courtSystemEnabled;
    }

    /**
     * @desc   - checks if Case Queue is enabled
     * @access - public
     * @param  -
     * @return - boolean : caseQueueEnabled
     */
    public isCaseQueueEnabled(): boolean
    {
        let caseQueueEnabled: boolean;
        let settings: any;
        let caseQueueSetting: any;

        caseQueueEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            caseQueueSetting =
                settings.find(
                    (item: any) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .CASE_QUEUE_MODULE_CODE
                        && item.value === "1"
                );
            caseQueueEnabled =
                !!caseQueueSetting;
        }

        return caseQueueEnabled;
    }

    /**
     * @desc   - checks if the Docket System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : docketEnabled
     */
    public isDocketSystemEnabled(): boolean
    {
        let docketEnabled: boolean;
        let settings: any;
        let docketSetting: any;

        docketEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            docketSetting =
                settings.find(
                    (item: any) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .DOCKET_SYSTEM_CODE
                        && item.value === "1"
                );
            docketEnabled =
                !!docketSetting;
        }

        return docketEnabled;
    }

    /**
     * @desc   - checks if Calendar (court calendar) is enabled
     * @access - public
     * @param  -
     * @return - boolean : courtCalendarEnabled
     */
    public isCourtCalendarEnabled(): boolean
    {
        let courtCalendarEnabled: boolean;
        let settings: any;
        let courtCalendarSetting: any;

        courtCalendarEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            courtCalendarSetting =
                settings.find(
                    (item: any) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .COURT_CALENDAR_SYSTEM_CODE
                        && item.value === "1"
                );
            courtCalendarEnabled =
                !!courtCalendarSetting;
        }

        return courtCalendarEnabled;
    }

    /**
     * @desc   - checks if the Audit System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : auditEnabled
     */
    public isAuditSystemEnabled(): boolean
    {
        let auditEnabled: boolean;
        let settings: any;
        let auditSetting: any;

        auditEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            auditSetting =
                settings.find(
                    (item: any) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .CASE_AUDIT_SYSTEM_CODE
                        && item.value === "1"
                );
            auditEnabled =
                !!auditSetting;
        }

        return auditEnabled;
    }

    /**
     * @desc   - checks if the Probation System
     *           is enabled
     * @access - public
     * @param  -
     * @return - boolean : probationEnabled
     */
    public isProbationSystemEnabled(): boolean
    {
        let probationEnabled: boolean;
        let settings: any;
        let probationSetting: any;

        probationEnabled = false;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            probationSetting =
                settings.find(
                    (item: any) =>
                        Number(item.code) ===
                        SETTING_CODE_REF
                            .PROBATION_SYSTEM_CODE
                        && item.value === "1"
                );
            probationEnabled =
                !!probationSetting;
        }

        return probationEnabled;
    }

    /**
     * @desc   - returns the list of supported form template
     *           image keys from the system setting
     * @access - public
     * @param  -
     * @return - string[] : image key names
     */
    public getFormTemplateImageKeys(): string[]
    {
        let settings: SettingInf[];
        let imageSetting: SettingInf | undefined;
        let parsed: any;
        let result: string[];

        result = [];
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            imageSetting = settings.find(
                (item: any) =>
                    Number(item.code) ===
                    SETTING_CODE_REF
                        .FORM_TEMPLATE_IMAGES_CODE
            );
            if (imageSetting && imageSetting.value)
            {
                try
                {
                    parsed = JSON.parse(
                        imageSetting.value
                    );
                    if (Array.isArray(parsed))
                    {
                        result = parsed;
                    }
                }
                catch (e)
                {
                    console.error(
                        'getFormTemplateImageKeys parse:',
                        e
                    );
                }
            }
        }

        return result;
    }

    /**
     * @desc   - document screen view mode from system setting
     * @access - public
     * @param  -
     * @return - string : avatar or list
     */
    public getDocumentViewMode(): string
    {
        let settings: SettingInf[];
        let viewSetting: SettingInf | undefined;
        let value: string;
        let result: string;

        result = DOCUMENT_VIEW_VALUE.AVATAR;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            viewSetting = settings.find(
                (item: any) =>
                    Number(item.code) ===
                    SETTING_CODE_REF.DOCUMENT_VIEW_MODE_CODE
            );
            value = viewSetting?.value ?
                String(viewSetting.value).trim().toLowerCase() :
                '';
            if (value === DOCUMENT_VIEW_VALUE.LIST)
            {
                result = DOCUMENT_VIEW_VALUE.LIST;
            }
        }

        return result;
    }

    /**
     * @desc   - payment plan SMS reminder days from setting
     * @access - public
     * @param  -
     * @return - number : days before due date
     */
    public getPaymentPlanTextMessageDays(): number
    {
        let settings: SettingInf[];
        let daysSetting: SettingInf | undefined;
        let parsed: number;
        let result: number;

        result = 3;
        settings = this.getSettingsArray();

        if (settings.length > 0)
        {
            daysSetting = settings.find(
                (item: any) =>
                    Number(item.code) ===
                    SETTING_CODE_REF
                        .PAYMENT_PLAN_TEXT_MESSAGE_DAYS_CODE
            );
            if (daysSetting && daysSetting.value != null)
            {
                parsed = Number(daysSetting.value);
                if (!isNaN(parsed) && parsed >= 0)
                {
                    result = parsed;
                }
            }
        }

        return result;
    }
}

export { SettingService };
